import React, { useEffect, useMemo, useState } from "react";
import {
  listarRevendasDropdown,
  relatorioLicencasAtivas,
  relatorioMovimentacoes,
  relatorioFaturamentoRevenda,
  relatorioLicencasCanceladas,
} from "../services/api";
import downloadCsv from "../utils/downloadCsv";
import { formatDateTimeBr } from "../utils/formatDateBr";
import { Download, RefreshCw } from "lucide-react";

const TABS = [
  { id: "ativas", label: "Licenças ativas" },
  { id: "movimentos", label: "Movimentações" },
  { id: "canceladas", label: "Canceladas" },
  { id: "faturamento", label: "Faturamento" },
];

const fmtDate = formatDateTimeBr;

const fmtMoney = (v) =>
  v == null ? "-" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const RelatoriosLicencas = ({ token }) => {
  const now = new Date();
  const [tab, setTab] = useState("ativas");
  const [revendas, setRevendas] = useState([]);
  const [revendaId, setRevendaId] = useState("");
  const [produto, setProduto] = useState("");
  const [tipo, setTipo] = useState("");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [ano, setAno] = useState(String(now.getFullYear()));
  const [mes, setMes] = useState(String(now.getMonth() + 1));
  const [aplicarCarencia, setAplicarCarencia] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState([]);

  useEffect(() => {
    listarRevendasDropdown(token)
      .then((r) => setRevendas(Array.isArray(r.data) ? r.data : r.data?.items || []))
      .catch(() => setRevendas([]));
  }, [token]);

  const carregar = async () => {
    setLoading(true);
    try {
      if (tab === "ativas") {
        const { data } = await relatorioLicencasAtivas(token, {
          revendaId: revendaId || undefined,
          produto: produto || undefined,
        });
        setDados(Array.isArray(data) ? data : []);
      } else if (tab === "movimentos") {
        const { data } = await relatorioMovimentacoes(token, {
          revendaId: revendaId || undefined,
          produto: produto || undefined,
          tipo: tipo || undefined,
          de: de || undefined,
          ate: ate || undefined,
        });
        setDados(Array.isArray(data) ? data : []);
      } else if (tab === "canceladas") {
        const { data } = await relatorioLicencasCanceladas(token, {
          revendaId: revendaId || undefined,
          produto: produto || undefined,
          de: de || undefined,
          ate: ate || undefined,
        });
        setDados(Array.isArray(data) ? data : []);
      } else {
        const { data } = await relatorioFaturamentoRevenda(token, {
          revendaId: revendaId || undefined,
          ano: Number(ano),
          mes: Number(mes),
          aplicarCarencia,
        });
        setDados(data?.linhas || []);
      }
    } catch (e) {
      console.error(e);
      setDados([]);
      alert("Erro ao carregar relatório.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const totalFaturamento = useMemo(
    () => dados.reduce((s, r) => s + (Number(r.valorTotal) || 0), 0),
    [dados]
  );

  const exportar = () => {
    const nome = `relatorio-${tab}-${ano || "all"}-${mes || "all"}.csv`;
    downloadCsv(nome, dados);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Relatórios de licenças</h2>
            <p className="text-sm text-gray-500 dark:text-slate-300/80">
              Ativas, movimentações, canceladas e faturamento por revenda.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={carregar}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm dark:border-white/10"
            >
              <RefreshCw size={16} /> Atualizar
            </button>
            <button
              type="button"
              onClick={exportar}
              disabled={!dados.length}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm disabled:opacity-50"
            >
              <Download size={16} /> CSV
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                tab === t.id
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <select
            value={revendaId}
            onChange={(e) => setRevendaId(e.target.value)}
            className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10"
          >
            <option value="">Todas revendas</option>
            {revendas.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nomeFantasia || r.razaoSocial}
              </option>
            ))}
          </select>

          {(tab === "ativas" || tab === "movimentos" || tab === "canceladas") && (
            <select
              value={produto}
              onChange={(e) => setProduto(e.target.value)}
              className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10"
            >
              <option value="">Todos produtos</option>
              <option value="ZenGestao">ZenGestão</option>
              <option value="ZenPDV">ZenPDV</option>
              <option value="ZenMobile">ZenMobile</option>
              <option value="ZenTag">ZenTag</option>
              <option value="ZenPass">ZenPass</option>
            </select>
          )}

          {tab === "movimentos" && (
            <>
              <input type="date" value={de} onChange={(e) => setDe(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
              <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10">
                <option value="">Todos tipos</option>
                <option value="ativacao">Ativação</option>
                <option value="cancelamento">Cancelamento</option>
                <option value="alteracao_qtd">Alteração qtd</option>
                <option value="bloqueio">Bloqueio</option>
                <option value="desbloqueio">Desbloqueio</option>
                <option value="bonificacao">Bonificação</option>
                <option value="desbonificacao">Desbonificação</option>
                <option value="cancelamento_licenca">Cancelamento licença</option>
                <option value="reativacao">Reativação</option>
              </select>
            </>
          )}

          {tab === "canceladas" && (
            <>
              <input type="date" value={de} onChange={(e) => setDe(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
              <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
            </>
          )}

          {tab === "faturamento" && (
            <>
              <input type="number" min="2020" max="2100" value={ano} onChange={(e) => setAno(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" placeholder="Ano" />
              <input type="number" min="1" max="12" value={mes} onChange={(e) => setMes(e.target.value)} className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" placeholder="Mês" />
              <label className="flex items-center gap-2 text-sm px-2">
                <input type="checkbox" checked={aplicarCarencia} onChange={(e) => setAplicarCarencia(e.target.checked)} />
                Aplicar carência (1 mês)
              </label>
            </>
          )}
        </div>

        {tab === "faturamento" && (
          <div className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Total: {fmtMoney(totalFaturamento)}
          </div>
        )}

        {loading ? (
          <p className="text-gray-500">Carregando...</p>
        ) : (
          <div className="overflow-x-auto zen-card rounded-2xl border dark:border-white/10">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-white/5">
                <tr>
                  {tab === "ativas" && (
                    <>
                      <th className="text-left p-3">Revenda</th>
                      <th className="text-left p-3">Cliente</th>
                      <th className="text-left p-3">Produto</th>
                      <th className="text-left p-3">Módulo</th>
                      <th className="text-center p-3">Qtd</th>
                      <th className="text-left p-3">Última ativação</th>
                      <th className="text-center p-3">Ativa</th>
                    </>
                  )}
                  {tab === "movimentos" && (
                    <>
                      <th className="text-left p-3">Data</th>
                      <th className="text-left p-3">Revenda</th>
                      <th className="text-left p-3">Cliente</th>
                      <th className="text-left p-3">Produto</th>
                      <th className="text-left p-3">Módulo</th>
                      <th className="text-left p-3">Tipo</th>
                      <th className="text-center p-3">Qtd</th>
                      <th className="text-left p-3">Motivo</th>
                    </>
                  )}
                  {tab === "canceladas" && (
                    <>
                      <th className="text-left p-3">Revenda</th>
                      <th className="text-left p-3">Cliente</th>
                      <th className="text-left p-3">Produto</th>
                      <th className="text-left p-3">Cancelado em</th>
                      <th className="text-left p-3">Motivo</th>
                      <th className="text-center p-3">Bonificada</th>
                    </>
                  )}
                  {tab === "faturamento" && (
                    <>
                      <th className="text-left p-3">Revenda</th>
                      <th className="text-left p-3">Produto</th>
                      <th className="text-left p-3">Módulo</th>
                      <th className="text-center p-3">Qtd</th>
                      <th className="text-right p-3">Unit.</th>
                      <th className="text-right p-3">Total</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {dados.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-gray-500">
                      Nenhum registro. Movimentações passam a ser gravadas a partir de agora.
                    </td>
                  </tr>
                ) : tab === "ativas" ? (
                  dados.map((r, i) => (
                    <tr key={i} className="border-t dark:border-white/10">
                      <td className="p-3">{r.revendaNome}</td>
                      <td className="p-3">{r.clienteNome}</td>
                      <td className="p-3">{r.produto}</td>
                      <td className="p-3">{r.moduloNome || r.moduloCodigo || "-"}</td>
                      <td className="p-3 text-center">{r.quantidade}</td>
                      <td className="p-3">{fmtDate(r.ultimaAtivacao)}</td>
                      <td className="p-3 text-center">{r.licencaAtiva ? "Sim" : "Não"}</td>
                    </tr>
                  ))
                ) : tab === "movimentos" ? (
                  dados.map((r) => (
                    <tr key={r.id} className="border-t dark:border-white/10">
                      <td className="p-3">{fmtDate(r.ocorridoEm)}</td>
                      <td className="p-3">{r.revendaNome}</td>
                      <td className="p-3">{r.clienteNome}</td>
                      <td className="p-3">{r.produto}</td>
                      <td className="p-3">{r.moduloNome || "-"}</td>
                      <td className="p-3">{r.tipo}</td>
                      <td className="p-3 text-center">{r.quantidade}</td>
                      <td className="p-3">{r.motivo || "-"}</td>
                    </tr>
                  ))
                ) : tab === "canceladas" ? (
                  dados.map((r, i) => (
                    <tr key={r.id || i} className="border-t dark:border-white/10">
                      <td className="p-3">{r.revendaNome}</td>
                      <td className="p-3">{r.clienteNome}</td>
                      <td className="p-3">{r.produto}</td>
                      <td className="p-3">{fmtDate(r.canceladoEm || r.CanceladoEm)}</td>
                      <td className="p-3">{r.motivo || r.Motivo || "-"}</td>
                      <td className="p-3 text-center">
                        {(r.bonificada ?? r.Bonificada) ? "Sim" : "Não"}
                      </td>
                    </tr>
                  ))
                ) : (
                  dados.map((r, i) => (
                    <tr key={i} className="border-t dark:border-white/10">
                      <td className="p-3">{r.revendaNome}</td>
                      <td className="p-3">{r.produto}</td>
                      <td className="p-3">{r.moduloNome}</td>
                      <td className="p-3 text-center">{r.quantidade}</td>
                      <td className="p-3 text-right">{fmtMoney(r.valorUnitario)}</td>
                      <td className="p-3 text-right">{fmtMoney(r.valorTotal)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatoriosLicencas;
